"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_KALSHI_SUFFIX = exports.KALSHI_LEAGUE_PREFIXES = exports.toLeaguePrefix = exports.LEAGUES = void 0;
exports.getKalshiSuffixOptions = getKalshiSuffixOptions;
/**
 * Kalshi League Constants
 *
 * Maps human-friendly league abbreviations to their Kalshi series tickers.
 * Used to populate the category selector on the events page and as
 * default values when no favourite league is saved.
 *
 * @example
 * ```ts
 * import { LEAGUES } from "./const";
 * loadEvents(LEAGUES.NBA); // Fetches NBA game markets
 * ```
 */
// Defined in shared/ so the server can stamp the same split onto order_events;
// re-exported here, the client's established import site.
var kalshi_taxonomy_1 = require("../shared/kalshi-taxonomy");
Object.defineProperty(exports, "LEAGUES", { enumerable: true, get: function () { return kalshi_taxonomy_1.LEAGUES; } });
Object.defineProperty(exports, "toLeaguePrefix", { enumerable: true, get: function () { return kalshi_taxonomy_1.toLeaguePrefix; } });
const kalshi_taxonomy_2 = require("../shared/kalshi-taxonomy");
/**
 * Kalshi league *prefixes* for the events-page prefix selector.
 *
 * A full series ticker is a prefix + a market-type suffix (see
 * {@link getKalshiSuffixOptions}): `KXMLB` + `GAME` = `KXMLBGAME`,
 * `KXMLB` + `SPREAD` = `KXMLBSPREAD`. Values are derived from
 * {@link LEAGUES} so they always match the historic single-selector tickers.
 */
exports.KALSHI_LEAGUE_PREFIXES = [
    { value: (0, kalshi_taxonomy_2.toLeaguePrefix)(kalshi_taxonomy_2.LEAGUES.NBA), label: "🏀 NBA" },
    { value: (0, kalshi_taxonomy_2.toLeaguePrefix)(kalshi_taxonomy_2.LEAGUES.NFL), label: "🏈 NFL" },
    { value: (0, kalshi_taxonomy_2.toLeaguePrefix)(kalshi_taxonomy_2.LEAGUES.MLB), label: "⚾ MLB" },
    { value: (0, kalshi_taxonomy_2.toLeaguePrefix)(kalshi_taxonomy_2.LEAGUES.NHL), label: "🏒 NHL" },
    { value: (0, kalshi_taxonomy_2.toLeaguePrefix)(kalshi_taxonomy_2.LEAGUES.CBB), label: "🏀 NCAA M" },
    { value: (0, kalshi_taxonomy_2.toLeaguePrefix)(kalshi_taxonomy_2.LEAGUES.WCBB), label: "🏀 NCAA W" },
    { value: (0, kalshi_taxonomy_2.toLeaguePrefix)(kalshi_taxonomy_2.LEAGUES.CFB), label: "🏈 NCAA F" },
    { value: (0, kalshi_taxonomy_2.toLeaguePrefix)(kalshi_taxonomy_2.LEAGUES.MLS), label: "⚽ MLS" },
    { value: (0, kalshi_taxonomy_2.toLeaguePrefix)(kalshi_taxonomy_2.LEAGUES.CBSBL), label: "⚾ NCAA Baseball" },
    { value: (0, kalshi_taxonomy_2.toLeaguePrefix)(kalshi_taxonomy_2.LEAGUES.CHKY), label: "🏒 NCAA Hockey" },
    { value: (0, kalshi_taxonomy_2.toLeaguePrefix)(kalshi_taxonomy_2.LEAGUES.WNBA), label: "🏀 WNBA" },
];
// ---------------------------------------------------------------------------
// Market-type suffixes (the second half of a full series ticker)
//
// A suffix is appended to a league prefix to form the full ticker. Suffixes
// differ by sport (basketball has quarters, baseball has "first 5 innings"),
// so they are composed from reusable per-period groups and resolved per league
// via `getKalshiSuffixOptions`. To add a sport, build its groups with
// `periodSuffixes` and register them in `SUFFIXES_BY_PREFIX` — no duplication.
// ---------------------------------------------------------------------------
/**
 * The three markets offered for a single game period, in display order.
 * The winner/moneyline token is irregular across Kalshi (`GAME` and `F5` are
 * bare, while periods use a `WINNER` suffix), so it is passed explicitly.
 */
function periodSuffixes(spec) {
    return [
        { value: spec.winner, label: `${spec.label} Winner` },
        { value: `${spec.stem}SPREAD`, label: `${spec.label} Spread` },
        { value: `${spec.stem}TOTAL`, label: `${spec.label} Total` },
    ];
}
/** Build a period group whose winner market appends `WINNER` (halves, quarters). */
const winnerSuffixed = (stem, label) => periodSuffixes({ stem, winner: `${stem}WINNER`, label });
/** Build a period group whose winner market is the bare stem (full game, F5). */
const bareWinner = (stem, label) => periodSuffixes({ stem, winner: stem, label });
/**
 * Full-game markets, common to every sport. `GAME` (moneyline) is first so it
 * stays the default, reproducing the historic `…GAME` ticker. Its spread/total
 * tokens are bare (`SPREAD`/`TOTAL`, no `GAME` prefix), hence the custom labels.
 */
const FULL_GAME_SUFFIXES = [
    { value: "GAME", label: "Game (Moneyline)" },
    { value: "SPREAD", label: "Spread" },
    { value: "TOTAL", label: "Total" },
];
/** Half markets (1st and 2nd), shared by court/field sports and soccer. */
const HALF_SUFFIXES = [
    ...winnerSuffixed("1H", "1st Half"),
    ...winnerSuffixed("2H", "2nd Half"),
];
/** Quarter markets, for sports played in quarters (basketball, football). */
const QUARTER_SUFFIXES = [
    ...winnerSuffixed("1Q", "1st Quarter"),
    ...winnerSuffixed("2Q", "2nd Quarter"),
    ...winnerSuffixed("3Q", "3rd Quarter"),
    ...winnerSuffixed("4Q", "4th Quarter"),
];
/** Baseball first-5-innings markets (`KXMLBF5`, `KXMLBF5SPREAD`, `KXMLBF5TOTAL`). */
const BASEBALL_SUFFIXES = [
    ...bareWinner("F5", "First 5 Innings"),
];
/** Default suffix set (basketball/football/etc.): full game + halves + quarters. */
const DEFAULT_SUFFIXES = [
    ...FULL_GAME_SUFFIXES,
    ...HALF_SUFFIXES,
    ...QUARTER_SUFFIXES,
];
/** Soccer suffix set: full game + halves only (no quarters). */
const SOCCER_SUFFIXES = [
    ...FULL_GAME_SUFFIXES,
    ...HALF_SUFFIXES,
];
/**
 * Per-league suffix overrides. Leagues absent here use {@link DEFAULT_SUFFIXES}.
 * Register a sport once and it applies to every prefix mapped to its set.
 */
const SUFFIXES_BY_PREFIX = new Map([
    [(0, kalshi_taxonomy_2.toLeaguePrefix)(kalshi_taxonomy_2.LEAGUES.MLB), [...FULL_GAME_SUFFIXES, ...BASEBALL_SUFFIXES]],
    [(0, kalshi_taxonomy_2.toLeaguePrefix)(kalshi_taxonomy_2.LEAGUES.CBSBL), [...FULL_GAME_SUFFIXES, ...BASEBALL_SUFFIXES]],
    [(0, kalshi_taxonomy_2.toLeaguePrefix)(kalshi_taxonomy_2.LEAGUES.MLS), SOCCER_SUFFIXES],
]);
/**
 * Resolve the market-type suffix options for a league prefix. Falls back to the
 * default (quarters/halves) set for prefixes without a sport-specific override
 * — including custom prefixes the operator types in.
 */
function getKalshiSuffixOptions(prefix) {
    return (prefix && SUFFIXES_BY_PREFIX.get(prefix)) || DEFAULT_SUFFIXES;
}
/** Default selected suffix — preserves the historic `…GAME` default. */
exports.DEFAULT_KALSHI_SUFFIX = "GAME";
